import { FC, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Image from 'next/image'

import Auth from 'store/auth'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import ScrollEventWrap from 'containers/scroll-event-wrap/scroll-event-wrap'
import MaxWidthLimiter from 'containers/max-width-limiter/max-width-limiter'
import HtmlContainer from 'containers/html-container/html-container'
import BannerAuthAccess from 'components/banners/banner-auth-access/banner-auth-access'
import Recommendations from 'containers/recommendations/recommendations'
import AuthorLector from 'components/parts/author-lector/author-lector'
import ArticleTitle from '../parts/article-title/article-title'
import AttachedItems from '../../parts/attached-items/attached-items'
import Commentaries from '../../parts/commentaries/commentaries'
import CustomLector from '../../parts/custom-lector/custom-lector'
import BreadcrumbsPage from 'components/parts/breadcrumb-page/breadcrumb-page'
import useLectorsSubscription from 'hooks/useLectorsSubscription'
import useProgressBar from 'hooks/useProgressBar'
import usePCWindow from 'hooks/usePCWindow'

import { lectorOrigin } from 'utils/analytics/other/sub_lector/sub_lector'
import { originShare } from 'utils/analytics/other/share_material/share_material'
import { defineContentStatus } from './utils'
import { PLACEFLAG_ARTICLE, A_ARTICLE_SCROLL } from 'constants/analytic-data'
import { HtmlContentStatus } from 'constants/article-data'
import { STATIC_PREVIEW_URL, URL_SITE, currImageDomain } from 'constants/data'
import { ScreenSize } from 'constants/media-constanst'
import { originSubscribe, originFWebinar } from 'utils/analytics/webinars/webinar_future'
import { A_art_openArticle, originArticle } from 'utils/analytics/library/article'
import { IArticle } from './article-material.type'
import {
  useARegAndLogin,
  originReg,
  A_LOG_REG_HASH,
  originRegWebinars,
} from 'utils/analytics/other/registration/registration'

import styles from '../articles.module.scss'
import videoMaterialStyle from 'styles/common/common-material.module.scss'
import LikeShare from 'components/parts/like-share/like-share'

const CustomAuthor = (props: any) => <AuthorLector {...props} modificator={styles.author} />

const Article: FC<IArticle> = ({
  id,
  name,
  startDate,
  Themes,
  htmlContent,
  Lecturs,
  lecturName,
  filesList,
  Comments,
  unregisteredAvailable,
  recomendation,
  isSpecProject,
  isHideComments,
  specPreviews,
}: IArticle) => {
  const { isAuthorized } = Auth.useAuth()
  const containerRef = useRef<HTMLDivElement>(null)

  const isMobile = usePCWindow(ScreenSize.mobile)

  const getImagePath = () => {
    if (isMobile) {
      return specPreviews?.mobile ? specPreviews?.mobile[0].path : null
    } else {
      return specPreviews?.desktop ? specPreviews?.desktop[0].path : null
    }
  }
  const ImagePath = getImagePath()

  const { lectors, subscribeLector } = useLectorsSubscription(Lecturs, PLACEFLAG_ARTICLE)

  const [commentsAmount, setCommentsAmount] = useState(0)

  const contentStatus = defineContentStatus(isAuthorized, unregisteredAvailable, recomendation)
  const GAHandler = () => {
    sessionStorage.setItem(A_LOG_REG_HASH, originRegWebinars.LOGIN)
  }

  useProgressBar()
  useARegAndLogin(originReg.ARTICLE)
  useEffect(() => {
    A_art_openArticle(originArticle.NOORIGIN, id)
  }, [])

  return (
    <ScrollEventWrap containerRef={containerRef} event={A_ARTICLE_SCROLL}>
      <div
        className={clsx(styles.article, {
          [styles.articleSpec]: isSpecProject,
        })}
      >
        <div className={styles.articleWrapper}>
          <div className={styles.breadcrumb}>
            <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
              <BreadcrumbsPage
                breadData={[
                  { name: 'Библиотека', link: `${URL_SITE}/library` },
                  { name, link: '' },
                ]}
              ></BreadcrumbsPage>
            </WidthLimiter>
          </div>

          {!isSpecProject && (
            <ArticleTitle
              themes={Themes}
              title={name}
              startDate={startDate}
              comments={commentsAmount}
            />
          )}

          <div
            className={clsx({
              [videoMaterialStyle.twoColumn]: !isSpecProject,
              [videoMaterialStyle.oneColumn]: isSpecProject,
            })}
          >
            {isSpecProject && !ImagePath && <div className={styles.articleSpecTitle}>{name}</div>}
            <MaxWidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
              <div
                className={clsx(styles.whiteBg, {
                  [styles.whiteBgSpec]: isSpecProject,
                })}
              >
                <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
                  {isSpecProject && ImagePath && (
                    <div>
                      <Image
                        src={`${currImageDomain}${STATIC_PREVIEW_URL}${ImagePath}`}
                        className={styles.specHeader}
                        width="1028"
                        height="500"
                        alt="Превью статьи"
                        unoptimized={true}
                      />
                    </div>
                  )}

                  <main className={styles.content} ref={containerRef}>
                    <HtmlContainer
                      htmlContent={htmlContent}
                      contentStatus={contentStatus}
                      recomendation={recomendation}
                    />

                    {contentStatus === HtmlContentStatus.HIDE_CONTENT && (
                      <div onClick={GAHandler}>
                        <BannerAuthAccess txt="Чтобы прочитать статью" />
                      </div>
                    )}
                  </main>
                  <div className={styles.bottom}>
                    <LikeShare AData={{ origin: originShare.ARTICLE }} />
                  </div>
                </WidthLimiter>
              </div>
              <WidthLimiter noTopPadding noBottomPadding noLeftPadding>
                {filesList && Boolean(filesList.length) && (
                  <div className={styles.attached}>
                    <AttachedItems items={filesList} />
                  </div>
                )}
              </WidthLimiter>
              <div className={styles.lectorsWrapper}>
                <h2 className={styles.authotTitle}>Авторы</h2>
                <div
                  className={clsx({
                    [styles.lectorsContainerOne]: lectors.length == 1,
                    [styles.lectorsContainer]: isSpecProject,
                  })}
                >
                  {lectors &&
                    lectors.map((lectur) => (
                      <AuthorLector
                        key={lectur.id}
                        lectorData={{ ...lectur, small: true }}
                        subscribeLector={subscribeLector}
                        modificator={styles.author}
                        withRegalia
                        origin={lectorOrigin.ARTICLE}
                        withLink
                      />
                    ))}
                </div>
                <div className={styles.customLector}>
                  <CustomLector lecturName={lecturName} LectorComponent={CustomAuthor} />
                </div>
              </div>
              {!isHideComments && (
                <div
                  className={clsx(styles.commentariesTop, {
                    [styles.commentariesLimited]: isSpecProject,
                  })}
                >
                  <Commentaries
                    comments={Comments}
                    materialType="article"
                    id={id}
                    getCommentsAmount={setCommentsAmount}
                  />
                </div>
              )}
            </MaxWidthLimiter>
            {!isSpecProject && (
              <section className={styles.recomendationSection}>
                <MaxWidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
                  <Recommendations
                    isVertical
                    noMargin
                    noPadding
                    isVerticalMobile
                    AData={{
                      originSub: originSubscribe.NOORIGIN,
                      originClick: originFWebinar.NOORIGIN,
                      originForArticle: originArticle.RECOMENDATION,
                    }}
                    eventWrapperModificator={videoMaterialStyle.eventWrapperModificator}
                    cardModificator={videoMaterialStyle.cardModificator}
                  />
                </MaxWidthLimiter>
              </section>
            )}
          </div>
        </div>
      </div>
    </ScrollEventWrap>
  )
}

export default Article
